import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import Div from "@jumbo/shared/Div";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { capitalizeFLetter, toFixed } from "@jumbo/utils";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { t } from "i18next";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { QRCodeSVG } from "qrcode.react";
import { ORDER_TRACKING_URL } from "app/services/config";
import { getVatRates } from "app/utils/appHelpers";
import ScheduleIcon from "@mui/icons-material/Schedule";

import {
  getEkasaErrorText,
  mapEkasaStatus,
  parseEkasaError,
  mapPaymentStatus,
  mapTerminalStatus,
} from "./orderStatusMapper";
import { getActiveDiscounts } from "./discountHelpers";
import {
  buildVatBreakdownRows,
  formatMoneyFromCents,
} from "./vatSummaryHelpers";
import {
  DemoIcon,
  NotExecutedIcon,
  UserCancelIcon,
} from "./orderStatusIcons";
import { useSnackbar } from "notistack";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import {
  changeOrderPreparationStatus,
  resendOrderNotification,
  reversePayment,
} from "app/services/api/management";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import {
  PREPARATION_STATUS_LIST,
  PREPARATION_STATUSES,
  allowedNextStatuses,
  isNotifiablePreparationStatus,
  preparationStatusColor,
  preparationStatusLabelKey,
} from "./preparationStatusMeta";
import {
  isExternalOrder,
  getDeliveryTypeLabelKey,
  getSourceChannelLabelKey,
  getSourceChannelDisplay,
  getPaymentPlaceLabelKey,
  formatOrderCustomerAddress,
} from "./externalOrderHelpers";

const StatusIcon = ({ status }) =>
  status ? (
    <CheckCircleIcon sx={{ color: "#2C9E4B" }} />
  ) : (
    <HighlightOffIcon sx={{ color: "#DA1113" }} />
  );
const USER_CANCELLED_PAYMENT_LABEL = "Platba bola zrušená zákazníkom";

const PendingIcon = () => <ScheduleIcon sx={{ color: "#6B7280" }} />;

const isDevDiagnosticsEnabled = () => {
  try {
    return (
      process.env.NODE_ENV !== "production" &&
      typeof window !== "undefined" &&
      window.location &&
      window.location.hostname === "localhost"
    );
  } catch (_e) {
    return false;
  }
};

const StatusStateIcon = ({ state, debugReason, title }) => {
  const icon =
    state === "ok" ? (
      <CheckCircleIcon sx={{ color: "#2C9E4B" }} />
    ) : state === "error" ? (
      <HighlightOffIcon sx={{ color: "#DA1113" }} />
    ) : state === "pending" ? (
      <PendingIcon />
    ) : state === "demo" ? (
      <DemoIcon />
    ) : state === "cancelled" ? (
      <UserCancelIcon />
    ) : (
      <NotExecutedIcon />
    );

  if (title) {
    return (
      <Tooltip title={title}>
        <Box component="span" sx={{ display: "inline-flex" }}>
          {icon}
        </Box>
      </Tooltip>
    );
  }
  if (!isDevDiagnosticsEnabled() || !debugReason) return icon;
  return (
    <Tooltip title={debugReason}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        {icon}
      </Box>
    </Tooltip>
  );
};
const ProductTable = ({ products, currencyCode, order }) => {
  const [page, setPage] = useState(0);
  const rowsPerPage = 50;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const lineItems = React.useMemo(() => {
    const activeDiscounts = getActiveDiscounts(order);
    const discountRows = activeDiscounts.map((d, idx) => {
      const qtyRaw = d?.raw?.quantity?.amount ?? 1;
      const qty = Number.isFinite(Number(qtyRaw)) ? Number(qtyRaw) : 1;
      const totalPrice =
        d.computedLinePrice != null ? Number(d.computedLinePrice) : null;

      const unitPriceRaw = d?.raw?.unitPrice;
      const unitPrice =
        unitPriceRaw != null && unitPriceRaw !== ""
          ? Number(unitPriceRaw)
          : totalPrice != null && qty
            ? totalPrice / qty
            : null;

      const vatRateRaw = d?.raw?.vatRate;
      const vatRate =
        vatRateRaw != null && vatRateRaw !== "" ? Number(vatRateRaw) : null;

      const vatBaseRaw = d?.raw?.vatBase;
      const vatRaw = d?.raw?.vat;
      let vatBase =
        vatBaseRaw != null && vatBaseRaw !== "" ? Number(vatBaseRaw) : null;
      let vat = vatRaw != null && vatRaw !== "" ? Number(vatRaw) : null;

      // Compute missing VAT parts if possible.
      if (
        (vatBase == null || vat == null) &&
        totalPrice != null &&
        vatRate != null
      ) {
        const denom = 1 + vatRate / 100;
        if (denom > 0) {
          const base = totalPrice / denom;
          const v = totalPrice - base;
          if (vatBase == null) vatBase = base;
          if (vat == null) vat = v;
        }
      }

      const nameNode = (
        <Box sx={{ color: "text.secondary" }}>
          <Typography variant="body2" sx={{ fontStyle: "italic", mb: 0 }}>
            {d.name ||
              t("pages.dsokOrders.discount.fallbackName", {
                defaultValue: "Discount",
              })}
          </Typography>
          {d.code ? (
            <Typography variant="caption" color="text.secondary">
              {t("pages.dsokOrders.discount.codeLabel", {
                defaultValue: "Code",
              })}
              {": "}
              {d.code}
            </Typography>
          ) : null}
        </Box>
      );

      return {
        rowType: "discount",
        key: `discount-${d.key}-${idx}`,
        name: nameNode,
        itemPrice: unitPrice,
        qty,
        vatRate: vatRate != null ? String(vatRate) : "—",
        vatBase,
        vat,
        totalPrice,
      };
    });

    // Expand each product with its selected add-ons (bindings) as indented sub-rows.
    // Only order-line bindings (string name) are rendered; catalog binding-group
    // definitions (name is an object) are skipped so kiosk orders are unaffected.
    const productList = Array.isArray(products) ? products : [];
    // Ids present in the order. A kiosk add-on line is one whose parentId points to
    // ANOTHER product in the same order; a standalone product has no such parent → no "+".
    const productIds = new Set(
      productList.map((p) => String(p?.id ?? p?.sku ?? "")).filter(Boolean),
    );
    const productRows = productList.flatMap((p) => {
      const parentRef = p?.parentId != null ? String(p.parentId) : "";
      const selfId = String(p?.id ?? p?.sku ?? "");
      const isChildLine =
        parentRef !== "" && parentRef !== selfId && productIds.has(parentRef);
      const self = isChildLine ? { ...p, isBinding: true } : p;

      // External add-ons live as nested bindings on the product line.
      const bindings = Array.isArray(p?.bindings) ? p.bindings : [];
      const selected = bindings.filter(
        (b) => b && typeof b?.name === "string" && b.name.trim() !== "",
      );
      const subRows = selected.map((b, bi) => {
        const price = Number(b?.price) || 0;
        const qty = Number(b?.quantity) || 1;
        return {
          rowType: "binding",
          isBinding: true,
          key: `${p?.id || p?.sku || "p"}-binding-${bi}`,
          name: b.name,
          itemPrice: price,
          qty,
          vatRate: "—",
          vatBase: null,
          vat: null,
          totalPrice: price ? price * qty : 0,
        };
      });
      return [self, ...subRows];
    });

    return [...productRows, ...discountRows];
  }, [order, products]);

  const visibleRows = React.useMemo(
    () => lineItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [lineItems, page],
  );
  const vatRates = getVatRates();
  const vatBreakdown = React.useMemo(
    () => buildVatBreakdownRows({ order, vatRates }),
    [order, vatRates],
  );
  return (
    <Paper>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: { md: 650, xs: 0 } }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("pages.blocks.moduleName")}</TableCell>
              <TableCell>{t("pages.dsokOrders.itemPrice")}</TableCell>
              <TableCell>{t("pages.dsokOrders.quantity")}</TableCell>
              <TableCell>{t("pages.dsokReports.VATRate")}</TableCell>
              <TableCell>{t("pages.dsokReports.totalWithoutVAT")}</TableCell>
              <TableCell>{t("pages.dsokReports.VAT")}</TableCell>
              <TableCell>{t("pages.dsokOrders.totalPrice")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow
                key={row?.key ?? index}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {row.isBinding ? (
                    <Box sx={{ pl: 2, color: "text.secondary" }}>
                      <Typography variant="body2" component="span">
                        +{" "}
                        {typeof row.name === "object" && row.name !== null
                          ? row.name.sk || row.name.en || ""
                          : row.name}
                      </Typography>
                    </Box>
                  ) : typeof row.name === "object" && row.name !== null ? (
                    row.name.sk || row.name.en || ""
                  ) : (
                    row.name
                  )}
                </TableCell>
                <TableCell>
                  {row.itemPrice === null || row.itemPrice === undefined
                    ? "—"
                    : `${toFixed(row.itemPrice, 2)} ${currencyCode || ""}`.trim()}
                </TableCell>
                <TableCell>{row.qty}</TableCell>
                <TableCell>{row.vatRate}</TableCell>
                <TableCell>
                  {row.vatBase === null || row.vatBase === undefined
                    ? "—"
                    : `${toFixed(row.vatBase, 2)} ${currencyCode || ""}`.trim()}
                </TableCell>
                <TableCell>
                  {row.vat === null || row.vat === undefined
                    ? "—"
                    : `${toFixed(row.vat, 2)} ${currencyCode || ""}`.trim()}
                </TableCell>
                <TableCell>
                  {row.totalPrice === null || row.totalPrice === undefined
                    ? "—"
                    : `${toFixed(row.totalPrice, 2)} ${currencyCode || ""}`.trim()}
                </TableCell>
              </TableRow>
            ))}
            {vatRates.map((rate) => {
              // VAT rows are rendered from a single source of truth (backend totals + adjusted breakdown).
              // We keep the loop for stable ordering based on vatRates, but values come from vatBreakdown.
              const row = vatBreakdown.rows.find((r) => r.key === rate.key);
              if (!row) return null;
              return (
                <TableRow key={`vat-${rate.key}`} selected>
                  <TableCell component="th" scope="row">
                    {t("pages.dsokReports.VAT")} {`${rate.value}`} %
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    {formatMoneyFromCents(toFixed, row.baseC, currencyCode)}
                  </TableCell>
                  <TableCell>
                    {formatMoneyFromCents(toFixed, row.vatC, currencyCode)}
                  </TableCell>
                  <TableCell>
                    {formatMoneyFromCents(toFixed, row.totalC, currencyCode)}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow selected>
              <TableCell component="th" scope="row">
                {t("pages.dsokOrders.total")}
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>
                {formatMoneyFromCents(
                  toFixed,
                  vatBreakdown.totals.baseTotalC,
                  currencyCode,
                )}
              </TableCell>
              <TableCell>
                {formatMoneyFromCents(
                  toFixed,
                  vatBreakdown.totals.vatTotalC,
                  currencyCode,
                )}
              </TableCell>
              <TableCell>
                {formatMoneyFromCents(
                  toFixed,
                  vatBreakdown.totals.totalC,
                  currencyCode,
                )}
              </TableCell>
            </TableRow>
            {order?.tip?.amount > 0 && (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t("pages.dsokOrders.tip", { defaultValue: "Prepitné" })}
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  {`${toFixed(order.tip.amount, 2)} ${
                    order.tip.currency || currencyCode || ""
                  }`.trim()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10]}
        component="div"
        count={lineItems.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
      />
    </Paper>
  );
};

const GenericTable = ({ rows, type }) => {
  const [page, setPage] = useState(0);
  const rowsPerPage = 15;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const visibleRows = React.useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page],
  );

  return (
    <Paper>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: { md: 650, xs: 0 } }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("pages.dsokDetails.title")}</TableCell>
              <TableCell>{t("pages.dsokDetails.value")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => {
              const key =
                type === "ekasa" && row.key === "id" ? "UID" : row.key;
              return (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {key}
                  </TableCell>
                  <TableCell>{row.value}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[rowsPerPage]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
      />
    </Paper>
  );
};

// Znormalizuje históriu stavu prípravy z objednávky do jednotného tvaru pre
// zobrazenie. Preferuje preparationStatusHistory (preparation-only), fallback na
// jednotnú statusHistory filtrovanú na type="preparation".
const normalizePreparationHistory = (o) => {
  const legacy = Array.isArray(o?.preparationStatusHistory)
    ? o.preparationStatusHistory
    : [];
  if (legacy.length) {
    return legacy.map((h) => ({
      status: h.status,
      at: h.timestamp,
      changedBy: h.changedBy,
      cancelReason: h.cancelReason,
      source: h.source,
    }));
  }
  const timeline = Array.isArray(o?.statusHistory) ? o.statusHistory : [];
  return timeline
    .filter((h) => h.type === "preparation")
    .map((h) => ({
      status: h.status,
      at: h.at,
      changedBy: h.changedBy,
      cancelReason: h.cancelReason,
      source: h.source,
    }));
};

// Management stavu prípravy objednávky v detaile (bez modalu). Zobrazí všetky
// stavy s ich farbami — aktívne sú len povolené prechody z aktuálneho stavu — a
// pod nimi vždy viditeľnú históriu zmien. Zmenu posiela na V1 API
// /api/v1/orders/preparation-status (naň je naviazaný notifikačný systém).
const PreparationStatusManager = ({ t, order, onStatusChanged, changedBy }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [currentStatus, setCurrentStatus] = useState(
    order?.preparationStatus || "",
  );
  const [history, setHistory] = useState(() =>
    normalizePreparationHistory(order),
  );
  const [submitting, setSubmitting] = useState("");
  const [resending, setResending] = useState(false);
  const [reversing, setReversing] = useState(false);
  const { showDialog, hideDialog } = useJumboDialog();

  const allowed = allowedNextStatuses(currentStatus);

  // Storno platby — len keď má objednávka uloženú referenciu na reverz (payment.provider)
  // a ešte nebola stornovaná.
  const canStorno =
    !!order?.payment?.provider &&
    order?.payment?.reversalState !== "reversed";

  const doStorno = async () => {
    hideDialog();
    if (reversing) return;
    setReversing(true);
    try {
      const data = await reversePayment({
        orderNumber: order?.externalOrderId ?? order?._id,
        customerId: order?.customerId,
        changedBy: changedBy || "admin",
      });
      if (data?.success) {
        // Card reversed. The eKasa fiscal storno (dobropis) is a separate step reported in
        // data.fiscal — if it failed, surface a warning (card money returned, dobropis pending).
        const fiscal = data?.fiscal;
        const fiscalFailed =
          fiscal && fiscal.success === false && fiscal.skipped !== true;
        if (fiscalFailed) {
          enqueueSnackbar(
            t(
              "pages.dsokOrders.storno.partialFiscalFailed",
              "Platba bola stornovaná, ale fiškálny dobropis zlyhal.",
            ),
            { variant: "warning" },
          );
        } else {
          enqueueSnackbar(
            t("pages.dsokOrders.storno.success", "Platba bola stornovaná."),
            { variant: "success" },
          );
        }
        if (onStatusChanged) onStatusChanged(currentStatus);
      } else {
        enqueueSnackbar(
          data?.message || t("pages.dsokOrders.storno.error", "Storno zlyhalo."),
          { variant: "error" },
        );
      }
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.message ||
          t("pages.dsokOrders.storno.error", "Storno zlyhalo."),
        { variant: "error" },
      );
    } finally {
      setReversing(false);
    }
  };

  const handleStorno = () => {
    if (reversing) return;
    showDialog({
      variant: "confirm",
      title: t("pages.dsokOrders.storno.confirmTitle", "Naozaj stornovať platbu?"),
      content: t(
        "pages.dsokOrders.storno.confirmText",
        "Platba sa stornuje na platobnom termináli. Túto akciu nie je možné vrátiť.",
      ),
      onYes: doStorno,
      onNo: () => hideDialog(),
    });
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      await resendOrderNotification({
        orderNumber: order?.externalOrderId ?? order?._id,
        customerId: order?.customerId,
      });
      enqueueSnackbar(
        t(
          "pages.dsokOrders.preparationManagement.resendSuccess",
          "Notifikácia bola odoslaná znova.",
        ),
        { variant: "success" },
      );
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        t(
          "pages.dsokOrders.preparationManagement.resendError",
          "Odoslanie notifikácie zlyhalo.",
        );
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setResending(false);
    }
  };

  const formatAt = (at) => {
    if (!at) return "";
    const m = moment(at);
    return m.isValid() ? m.format("DD.MM.YYYY HH:mm") : String(at);
  };

  const handleClick = async (status) => {
    if (!allowed.includes(status) || submitting) return;
    const isCancel = status === PREPARATION_STATUSES.CANCELED;
    setSubmitting(status);
    try {
      await changeOrderPreparationStatus({
        orderNumber: order?.externalOrderId ?? order?._id,
        status,
        customerId: order?.customerId,
        cancelReason: isCancel ? { code: "other", note: null } : undefined,
        changedBy: changedBy || "admin",
      });
      setHistory((prev) => [
        ...prev,
        {
          status,
          at: moment().format("YYYY-MM-DD HH:mm:ss"),
          changedBy: changedBy || "admin",
          cancelReason: isCancel ? { code: "other" } : null,
          source: "admin",
        },
      ]);
      setCurrentStatus(status);
      enqueueSnackbar(
        t(
          "pages.dsokOrders.preparationManagement.success",
          "Stav objednávky bol zmenený.",
        ),
        { variant: "success" },
      );
      if (onStatusChanged) onStatusChanged(status);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        t(
          "pages.dsokOrders.preparationManagement.error",
          "Zmena stavu zlyhala.",
        );
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSubmitting("");
    }
  };

  return (
    <Box sx={{ width: "100%", px: { md: 2, xs: 0 } }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h5" sx={{ mb: 1.5 }}>
        {t(
          "pages.dsokOrders.preparationManagement.title",
          "Management stavu objednávky",
        )}
      </Typography>

      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 3 }}>
        {PREPARATION_STATUS_LIST.map((s) => {
          const c = preparationStatusColor(s);
          const isCurrent = s === currentStatus;
          const isAllowed = allowed.includes(s);
          return (
            <Button
              key={s}
              onClick={() => handleClick(s)}
              disabled={!isAllowed || Boolean(submitting)}
              disableElevation
              startIcon={
                submitting === s ? (
                  <CircularProgress size={16} sx={{ color: c.color }} />
                ) : null
              }
              sx={{
                bgcolor: c.bg,
                color: c.color,
                border: `2px solid ${isCurrent ? c.color : "transparent"}`,
                fontWeight: 700,
                textTransform: "none",
                opacity: isAllowed || isCurrent ? 1 : 0.4,
                "&:hover": { bgcolor: c.bg, filter: "brightness(0.95)" },
                "&.Mui-disabled": {
                  bgcolor: c.bg,
                  color: c.color,
                  opacity: isCurrent ? 0.9 : 0.4,
                  border: `2px solid ${isCurrent ? c.color : "transparent"}`,
                },
              }}
            >
              {t(preparationStatusLabelKey(s), s)}
              {isCurrent
                ? ` • ${t(
                    "pages.dsokOrders.preparationManagement.currentShort",
                    "aktuálny",
                  )}`
                : ""}
            </Button>
          );
        })}
      </Stack>

      {isNotifiablePreparationStatus(currentStatus) ? (
        <Button
          variant="text"
          size="small"
          onClick={handleResend}
          disabled={resending}
          startIcon={
            resending ? (
              <CircularProgress size={16} />
            ) : (
              <NotificationsActiveOutlinedIcon />
            )
          }
          sx={{ mb: 3, textTransform: "none" }}
        >
          {t(
            "pages.dsokOrders.preparationManagement.resend",
            "Poslať notifikáciu znova",
          )}
        </Button>
      ) : null}

      {canStorno ? (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleStorno}
          disabled={reversing}
          startIcon={reversing ? <CircularProgress size={16} /> : null}
          sx={{ mb: 3, ml: 1, textTransform: "none" }}
        >
          {t("pages.dsokOrders.storno.button", "Storno platby")}
        </Button>
      ) : null}

      {order?.payment?.reversalState === "reversed" ? (
        <Typography
          variant="body2"
          sx={{ mb: 3, color: "warning.main", fontWeight: 500 }}
        >
          {t("pages.dsokOrders.storno.reversedLabel", "Stornované")}
          {order?.payment?.reversalAt
            ? " " +
              moment(order.payment.reversalAt).format("DD.MM.YYYY HH:mm")
            : ""}
          {order?.payment?.reversalBy
            ? ` (${order.payment.reversalBy})`
            : ""}
        </Typography>
      ) : null}

      {order?.ekasaStorno?.state === "done" ? (
        <Typography
          variant="body2"
          sx={{ mb: 3, color: "warning.main", fontWeight: 500 }}
        >
          {t("pages.dsokOrders.storno.fiscalDone", "Dobropis vystavený")}
          {order?.ekasaStorno?.at
            ? " " + moment(order.ekasaStorno.at).format("DD.MM.YYYY HH:mm")
            : ""}
        </Typography>
      ) : order?.ekasaStorno?.state === "failed" ? (
        <Typography
          variant="body2"
          sx={{ mb: 3, color: "error.main", fontWeight: 500 }}
        >
          {t("pages.dsokOrders.storno.fiscalFailed", "Fiškálny dobropis zlyhal")}
          {order?.ekasaStorno?.error?.message
            ? `: ${order.ekasaStorno.error.message}`
            : ""}
        </Typography>
      ) : order?.ekasaStorno?.state === "pending" ? (
        <Typography
          variant="body2"
          sx={{ mb: 3, color: "text.secondary", fontWeight: 500 }}
        >
          {t("pages.dsokOrders.storno.fiscalPending", "Fiškálny dobropis prebieha…")}
        </Typography>
      ) : null}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
        {t(
          "pages.dsokOrders.preparationManagement.history",
          "História zmien stavu",
        )}
      </Typography>
      {history.length ? (
        <Stack gap={0.75}>
          {history.map((h, i) => {
            const c = preparationStatusColor(h.status);
            return (
              <Stack
                key={i}
                direction="row"
                gap={1}
                alignItems="center"
                sx={{ flexWrap: "wrap" }}
              >
                <Box
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: "12px",
                    fontWeight: 600,
                    bgcolor: c.bg,
                    color: c.color,
                    minWidth: 104,
                    textAlign: "center",
                  }}
                >
                  {t(preparationStatusLabelKey(h.status), h.status)}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatAt(h.at)}
                </Typography>
                {h.changedBy ? (
                  <Typography variant="caption" color="text.secondary">
                    · {h.changedBy}
                  </Typography>
                ) : null}
                {h.cancelReason?.code ? (
                  <Typography variant="caption" color="text.secondary">
                    ·{" "}
                    {t(
                      `pages.dsokOrders.preparationManagement.cancelReasons.${h.cancelReason.code}`,
                      h.cancelReason.code,
                    )}
                  </Typography>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {t(
            "pages.dsokOrders.preparationManagement.noHistory",
            "Zatiaľ žiadne zmeny stavu.",
          )}
        </Typography>
      )}
    </Box>
  );
};

/**
 * External-order sections (source, customer contact, delivery/address, note, payment place)
 * rendered at the top of the general tab. Only for orders received via the external API.
 */
const ExternalOrderInfo = ({ order, t }) => {
  if (!isExternalOrder(order)) return null;
  const source = order?.source || {};
  const channel = source.channel || order?.orderSource || "";
  const channelKey = getSourceChannelLabelKey(channel);
  const deliveryKey = getDeliveryTypeLabelKey(order?.deliveryType);
  const paymentPlaceKey = getPaymentPlaceLabelKey(order?.payment?.method);
  const customer = order?.orderCustomer || null;
  const address = customer?.address || null;
  const addressLine = formatOrderCustomerAddress(address);

  const Field = ({ label, children }) =>
    children ? (
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {children}
        </Typography>
      </Box>
    ) : null;

  // Stav úhrady podľa objednávky (externý systém posiela payment.paid; fallback na orderStatus).
  const isPaid =
    order?.payment?.paid === true ||
    order?.payment?.isPaid === true ||
    order?.orderStatus === "paid" ||
    order?.orderStatus === "complete";
  const paymentPlaceText = [
    paymentPlaceKey ? t(paymentPlaceKey) : order?.payment?.method,
    isPaid
      ? t("pages.dsokOrders.external.paid", { defaultValue: "Uhradené" })
      : t("pages.dsokOrders.external.unpaid", { defaultValue: "Neuhradené" }),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: "#F8FAFF",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        {t("pages.dsokOrders.external.sectionTitle", {
          defaultValue: "Externá objednávka",
        })}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">
            {t("pages.dsokOrders.external.sourceSection", {
              defaultValue: "Zdroj",
            })}
          </Typography>
          <Field
            label={t("pages.dsokOrders.external.system", {
              defaultValue: "Systém",
            })}
          >
            {getSourceChannelDisplay(source.system, t)}
          </Field>
          <Field
            label={t("pages.dsokOrders.external.channel", {
              defaultValue: "Kanál",
            })}
          >
            {getSourceChannelDisplay(channel, t)}
          </Field>
          <Field
            label={t("pages.dsokOrders.external.externalReference", {
              defaultValue: "Referencia",
            })}
          >
            {source.externalReference}
          </Field>
          <Field
            label={t("pages.dsokOrders.external.paymentPlace", {
              defaultValue: "Úhrada",
            })}
          >
            {paymentPlaceText}
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">
            {t("pages.dsokOrders.external.customerSection", {
              defaultValue: "Zákazník",
            })}
          </Typography>
          <Field
            label={t("pages.dsokOrders.external.name", { defaultValue: "Meno" })}
          >
            {customer?.name}
          </Field>
          <Field
            label={t("pages.dsokOrders.external.phone", {
              defaultValue: "Telefón",
            })}
          >
            {customer?.phone ? (
              <Link href={`tel:${customer.phone}`}>{customer.phone}</Link>
            ) : null}
          </Field>
          <Field
            label={t("pages.dsokOrders.external.email", {
              defaultValue: "Email",
            })}
          >
            {customer?.email ? (
              <Link href={`mailto:${customer.email}`}>{customer.email}</Link>
            ) : null}
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">
            {t("pages.dsokOrders.external.deliverySection", {
              defaultValue: "Doručenie",
            })}
          </Typography>
          <Field
            label={t("pages.dsokOrders.external.deliveryType", {
              defaultValue: "Typ",
            })}
          >
            {deliveryKey ? t(deliveryKey, order?.deliveryType) : order?.deliveryType}
          </Field>
          <Field
            label={t("pages.dsokOrders.external.address", {
              defaultValue: "Adresa",
            })}
          >
            {addressLine}
          </Field>
          <Field
            label={t("pages.dsokOrders.external.addressNote", {
              defaultValue: "Poznámka k adrese",
            })}
          >
            {address?.note}
          </Field>
        </Grid>
        {order?.note ? (
          <Grid item xs={12}>
            <Field
              label={t("pages.dsokOrders.external.note", {
                defaultValue: "Poznámka k objednávke",
              })}
            >
              {order.note}
            </Field>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

const GeneralInformation = ({
  t,
  values,
  order,
  currencyCode,
  demo,
  posUserCancel,
  orderManagementStatus,
  paymentMethod,
  paymentUi,
  ekasaUi,
  lineOfBusiness,
  orderNotificationsEnabled,
  onStatusChanged,
  changedBy,
}) => {
  const paymentMethodType =
    t(`pages.dsokOrders.paymentMethods.${paymentMethod}`, {
      defaultValue: String(paymentMethod || ""),
    }) || String(paymentMethod || "");
  // Pre concreteStation zákazníkov je QR kód objednávky len z externalOrderId;
  // inak z order.qrCode.
  const orderQrValue =
    lineOfBusiness === "concreteStation"
      ? order?.externalOrderId
      : order?.qrCode;
  // Tracking URL sa vytvára na BACKENDE pri objednávke (order.trackingUrl) — podľa
  // prostredia (staging/prod). Frontend z nej len vyrenderuje QR. Fallback (staré
  // objednávky / ORDER_TRACKING_BASE_URL nenastavené) na frontend config.
  const trackingUrl =
    order?.trackingUrl ||
    (order?.externalOrderId
      ? `${ORDER_TRACKING_URL}/order/${order.externalOrderId}`
      : null);
  const qrSection =
    orderQrValue || trackingUrl ? (
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        {orderQrValue ? (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              {t("pages.dsokOrders.orderQrCode", {
                defaultValue: "QR kód objednávky",
              })}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                p: 0.5,
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <QRCodeSVG value={String(orderQrValue)} size={92} />
            </Box>
          </Box>
        ) : null}
        {trackingUrl ? (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              {t("pages.dsokOrders.trackingQrCode", {
                defaultValue: "QR kód sledovania objednávky",
              })}
            </Typography>
            <Tooltip
              title={t("pages.dsokOrders.openTracking", {
                defaultValue: "Otvoriť sledovaciu stránku",
              })}
            >
              <Link
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  p: 0.5,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  cursor: "pointer",
                }}
              >
                <QRCodeSVG value={trackingUrl} size={92} />
              </Link>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
    ) : null;

  return (
    <Stack direction="column" gap={4} width="100%">
      <ExternalOrderInfo order={order} t={t} />
      <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
        {/* Ľavý stĺpec — vstupné polia (read-only) + stavové ikony */}
        <Grid item xs={12} md={7}>
          <fieldset
            disabled
            style={{ border: "none", padding: 0, margin: 0 }}
          >
            <Grid container columnSpacing={2}>
              <Grid item md={6} xs={12}>
                <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                  <JumboTextField
                    fullWidth
                    name={`createdAt`}
                    label={t("pages.presentation.createdAt")}
                  />
                </Div>
              </Grid>
              <Grid item md={6} xs={12}>
                <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                  <JumboTextField
                    fullWidth
                    name={`orderStatus`}
                    label={t("pages.dsokOrders.orderStatus")}
                  />
                </Div>
              </Grid>
              <Grid item md={6} xs={12}>
                <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                  <JumboTextField
                    fullWidth
                    name={`pickupCode`}
                    label={
                      t("pages.dsokOrders.pickupCode") +
                      `${
                        order?.locator
                          ? `/${t("pages.dsokOrders.locator")}`
                          : ""
                      }`
                    }
                  />
                </Div>
              </Grid>
              <Grid item md={6} xs={12}>
                <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                  <JumboTextField
                    fullWidth
                    name={`orderCode`}
                    label={t("pages.dsokOrders.orderCode")}
                  />
                </Div>
              </Grid>
              <Grid item md={6} xs={12}>
                <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                  <JumboTextField
                    fullWidth
                    name={`grandTotal`}
                    label={t("pages.dsokOrders.total")}
                  />
                </Div>
              </Grid>

              <Grid container item md={12} xs={12} columnSpacing={2}>
                <Grid
                  item
                  pb={2}
                  md={4}
                  xs={4}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      margin: "0 0 0 10px",
                      background: "white",
                      color: "text.secondary",
                      width: "fit-content",
                    }}
                  >
                    {orderManagementStatus && (
                      <>{t("pages.dsokOrders.orderSent")}/</>
                    )}
                    <span
                      style={{
                        display: "inline",
                      }}
                    >
                      {order?.internalOrderId &&
                        `${t("pages.presentation.id")}`}
                    </span>
                  </Typography>
                  <Typography
                    variant={"body1"}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    {posUserCancel && <UserCancelIcon />}
                    {!posUserCancel && (
                      <>
                        {orderManagementStatus && (
                          <StatusIcon status={order?.orderSentStatus} />
                        )}
                        {order?.internalOrderId}
                      </>
                    )}
                  </Typography>
                </Grid>
                {paymentMethod !== "invoice" && (
                  <>
                    <Grid
                      item
                      pb={2}
                      md={4}
                      xs={4}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-evenly",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          margin: "0 0 0 10px",
                          background: "white",
                          color: "text.secondary",
                          width: "fit-content",
                        }}
                      >
                        {t("pages.dsokOrders.payment")}
                      </Typography>
                      <StatusStateIcon
                        state={paymentUi?.state || "unknown"}
                        title={
                          paymentUi?.state === "cancelled"
                            ? USER_CANCELLED_PAYMENT_LABEL
                            : undefined
                        }
                        debugReason={
                          paymentUi?.debugReason
                            ? `payment: ${paymentUi.debugReason}`
                            : "payment: —"
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      pb={2}
                      md={4}
                      xs={4}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-evenly",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          margin: "0 0 0 10px",
                          background: "white",
                          color: "text.secondary",
                          width: "fit-content",
                        }}
                      >
                        {t("pages.dsokDetails.ekasa")}
                      </Typography>
                      <StatusStateIcon
                        state={ekasaUi?.state || "unknown"}
                        debugReason={
                          ekasaUi?.debugReason
                            ? `ekasa: ${ekasaUi.debugReason}`
                            : "ekasa: —"
                        }
                      />
                    </Grid>
                  </>
                )}
                {paymentMethod === "invoice" && (
                  <>
                    <Grid
                      item
                      pb={2}
                      md={4}
                      xs={4}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-evenly",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          margin: "0 0 0 10px",
                          background: "white",
                          color: "text.secondary",
                          width: "fit-content",
                        }}
                      >
                        {paymentMethodType}
                      </Typography>
                      <StatusStateIcon
                        state={paymentUi?.state || "unknown"}
                        title={
                          paymentUi?.state === "cancelled"
                            ? USER_CANCELLED_PAYMENT_LABEL
                            : undefined
                        }
                        debugReason={
                          paymentUi?.debugReason
                            ? `payment: ${paymentUi.debugReason}`
                            : "payment: —"
                        }
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </fieldset>
        </Grid>
        {/* Pravý stĺpec — QR kódy + management stavu objednávky */}
        <Grid item xs={12} md={5}>
          <Stack gap={3} sx={{ mt: { md: 2, xs: 1 } }}>
            {qrSection}
            {orderNotificationsEnabled ? (
              <PreparationStatusManager
                t={t}
                order={order}
                onStatusChanged={onStatusChanged}
                changedBy={changedBy}
              />
            ) : null}
          </Stack>
        </Grid>
      </Grid>
      {/* Zoznam položiek — celá šírka pod stĺpcami */}
      <Div sx={{ width: "100%" }}>
        <ProductTable
          order={order}
          products={Object.values(order?.products || {})}
          currencyCode={currencyCode}
        />
      </Div>
    </Stack>
  );
};

// Kľúče chyby eKasa, ktoré v tabuľke nezobrazujeme (duplikát celej chyby / prázdne metadáta).
const EKASA_HIDDEN_KEYS = new Set(["completeErrorMessage", "extensions"]);

/**
 * Rozloží objekt na riadky tabuľky: vnorené objekty ako "portosResponse.title",
 * polia ako JSON. Prázdne hodnoty (null / "") vynechá.
 */
const flattenToRows = (obj, prefix = "") =>
  Object.entries(obj || {}).flatMap(([key, value]) => {
    if (!prefix && EKASA_HIDDEN_KEYS.has(key)) return [];
    const path = prefix ? `${prefix}.${key}` : key;
    if (value === null || value === undefined || value === "") return [];
    if (Array.isArray(value)) return [{ key: path, value: JSON.stringify(value) }];
    if (typeof value === "object") return flattenToRows(value, path);
    return [{ key: path, value: String(value) }];
  });

const EKASA = ({ order }) => {
  const response =
    order?.eKasaResponse &&
    typeof order.eKasaResponse === "object" &&
    Object.keys(order.eKasaResponse).length > 0
      ? order.eKasaResponse
      : null;
  // eKasaError môže byť objekt aj JSON string (kiosk flow) → vždy parsovať.
  const error = response ? null : parseEkasaError(order);
  const isError = Boolean(error) && error?.status !== "no-error";
  // Pri DLA je samotná chyba zabalená v response.data.
  const errorData =
    isError && error?.response?.data && typeof error.response.data === "object"
      ? error.response.data
      : error;
  const rows = flattenToRows(response || errorData || {});
  const errorText = isError ? getEkasaErrorText(order) : "";

  return (
    <Stack direction="row" gap={4} width="100%" alignItems="center">
      <Stack width="100%">
        <Stack direction="row" gap={2} pl={2}>
          <Grid container columnSpacing={2} rowSpacing={2}>
            {errorText ? (
              <Grid item md={12} xs={12}>
                <Alert severity="error" sx={{ whiteSpace: "pre-wrap" }}>
                  {errorText}
                </Alert>
              </Grid>
            ) : null}
            <Grid item md={12} xs={12}>
              <GenericTable rows={rows} type="ekasa" />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </Stack>
  );
};

const POS = ({ order, terminalUi }) => {
  const selectedResponse =
    order.posResponse || order.posError || order.failedTransactionData || {};
  return (
    <Stack direction="row" gap={4} width="100%" alignItems="center">
      <Stack width="100%">
        <Stack direction="row" gap={2} pl={2}>
          <Grid container columnSpacing={2}>
            {terminalUi?.state === "cancelled" ? (
              <Grid item md={12} xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <StatusStateIcon
                    state="cancelled"
                    title={USER_CANCELLED_PAYMENT_LABEL}
                    debugReason={
                      terminalUi?.debugReason
                        ? `pos: ${terminalUi.debugReason}`
                        : "pos: user cancelled"
                    }
                  />
                  <Typography variant="body2">
                    {USER_CANCELLED_PAYMENT_LABEL}
                  </Typography>
                </Box>
              </Grid>
            ) : null}
            <Grid item md={12} xs={12}>
              <GenericTable
                rows={Object.keys(selectedResponse).map((key) => ({
                  key,
                  value:
                    typeof selectedResponse[key] !== "object" &&
                    selectedResponse[key],
                }))}
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </Stack>
  );
};

// ALBACON / Glory cash machine payment result (replaces the POS terminal tab for
// payment.method === "albaconCashMachine"). Shows the cashPayment sub-document.
const ALBACON = ({ order }) => {
  const cp = order?.cashPayment || {};
  return (
    <Stack direction="row" gap={4} width="100%" alignItems="center">
      <Stack width="100%">
        <Stack direction="row" gap={2} pl={2}>
          <Grid container columnSpacing={2}>
            <Grid item md={12} xs={12}>
              <GenericTable
                rows={Object.keys(cp).map((key) => ({
                  key,
                  value: typeof cp[key] !== "object" ? cp[key] : undefined,
                }))}
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </Stack>
  );
};

// Map cashPayment.status → status-icon state for the Albacon tab.
const albaconTabState = (order) => {
  const s = order?.cashPayment?.status;
  if (s === "approved") return "ok";
  if (s === "cancelled") return "cancelled";
  if (s === "pending") return "pending";
  if (
    ["shortage", "busy", "error", "failed", "timedOut", "reversalRequested"].includes(
      s
    )
  ) {
    return "error";
  }
  return "unknown";
};

const DSOKOrderForm = ({
  order,
  currencyCode,
  title,
  orderManagementStatus,
  enablePaymentStatus,
  enablePrintOrEkasaStatus,
  enableClientsInList,
  lineOfBusiness,
  orderNotificationsEnabled,
  onStatusChanged,
  changedBy,
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState("generalInformation");
  const paymentUi = React.useMemo(() => mapPaymentStatus(order), [order]);
  const terminalUi = React.useMemo(() => mapTerminalStatus(order), [order]);
  const ekasaUi = React.useMemo(() => mapEkasaStatus(order), [order]);
  const demo = order?.demo;
  let posUserCancel = false;
  if (
    order?.posError?.reason &&
    order?.posError?.reason === "EMV_USER_CANCEL"
  ) {
    posUserCancel = true;
  }
  const paymentMethod = order?.payment?.method || "card";
  const albaconUiState = React.useMemo(() => albaconTabState(order), [order]);
  let TABS = { generalInformation: GeneralInformation, pos: POS, ekasa: EKASA };
  // Cash machine (ALBACON/Glory): there is no card/POS terminal — show the Albacon
  // payment result instead of the POS tab.
  if (paymentMethod === "albaconCashMachine") {
    TABS = { generalInformation: GeneralInformation, albacon: ALBACON, ekasa: EKASA };
  }
  if (demo || paymentMethod === "invoice") {
    TABS = { generalInformation: GeneralInformation };
  }

  if (posUserCancel) {
    TABS = { generalInformation: GeneralInformation, pos: POS };
  }

  // External orders (e.g. AudioEcho) are unpaid and have no POS/eKasa/cash-machine
  // result — show only the general tab (with the external sections rendered inside it).
  if (isExternalOrder(order)) {
    TABS = { generalInformation: GeneralInformation };
  }
  return (
    <>
      <Formik
        validateOnChange={true}
        initialValues={{
          createdAt:
            moment(order?.createdAt).format("DD/MM/YYYY HH:mm:ss") || "",
          orderStatus:
            (order?.orderStatus && capitalizeFLetter(order?.orderStatus)) || "",
          posStatus: order?.posStatus || "",
          orderSentStatus: order?.orderSentStatus || "",
          ekasaStatus: order?.ekasaStatus || "",
          pickupCode:
            (order?.pickupCode || "") +
            `${order?.locator ? `/${order?.locator}` : ""}`,
          orderCode: order?.orderCode || "",
          grandTotal:
            `${toFixed(order?.grandTotal, 2)} ${currencyCode || ""}` || "",
        }}
      >
        {({ setFieldValue, values }) => (
          <Form style={{ textAlign: "left" }} noValidate>
            <Typography variant="h4" pl={2} pb={2} mt={0}>
              {title || ""}
            </Typography>
            <TabContext value={selectedTab}>
              <Stack sx={{ mb: 2 }}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <TabList
                    variant="scrollable"
                    visibleScrollbar
                    onChange={(_, val) => setSelectedTab(val)}
                  >
                    {Object.keys(TABS).map((tab) => {
                      const tabState =
                        tab === "pos"
                          ? terminalUi?.state
                          : tab === "ekasa"
                            ? ekasaUi?.state
                            : tab === "albacon"
                              ? albaconUiState
                              : null;
                      const tabDebug =
                        tab === "pos"
                          ? terminalUi?.debugReason
                          : tab === "ekasa"
                            ? ekasaUi?.debugReason
                            : tab === "albacon"
                              ? `albacon: ${order?.cashPayment?.status || "—"}`
                              : null;
                      return (
                        <Tab
                          key={tab}
                          label={
                            <Div
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {t(`pages.dsokDetails.${tab}`)}
                              {tab !== "generalInformation" ? (
                                <StatusStateIcon
                                  state={tabState || "unknown"}
                                  title={
                                    tab === "pos" && tabState === "cancelled"
                                      ? USER_CANCELLED_PAYMENT_LABEL
                                      : undefined
                                  }
                                  debugReason={
                                    tabDebug
                                      ? `${tab}: ${tabDebug}`
                                      : `${tab}: —`
                                  }
                                />
                              ) : null}
                            </Div>
                          }
                          value={tab}
                        />
                      );
                    })}
                  </TabList>
                </Box>
                {Object.keys(TABS).map((tab) => {
                  const SelectedTab = TABS[tab];
                  return (
                    <TabPanel
                      key={tab}
                      value={tab}
                      sx={{
                        pb: { md: 1, xs: 0 },
                        pr: 0,
                        pl: { md: 3, xs: 0 },
                        minHeight: 350,
                      }}
                    >
                      <SelectedTab
                        order={order}
                        values={values}
                        t={t}
                        currencyCode={currencyCode}
                        demo={demo}
                        posUserCancel={posUserCancel}
                        orderManagementStatus={orderManagementStatus}
                        paymentMethod={paymentMethod}
                        paymentUi={paymentUi}
                        terminalUi={terminalUi}
                        ekasaUi={ekasaUi}
                        lineOfBusiness={lineOfBusiness}
                        orderNotificationsEnabled={orderNotificationsEnabled}
                        onStatusChanged={onStatusChanged}
                        changedBy={changedBy}
                      />
                    </TabPanel>
                  );
                })}
              </Stack>
            </TabContext>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default DSOKOrderForm;
